import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ACard, inputDark } from "@/components/shared/AdminKit";
import { useTr, fmtDate } from "@/lib/tr";
import { Loader2, Star } from "lucide-react";

const fmtMin = (m, tr) => (m == null ? "—" : m < 60 ? `${m} min` : `${Math.round((m / 60) * 10) / 10} ${tr("h", "h")}`);

function Kpi({ label, value, hint, testid }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={testid}>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-white">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

export function SupportPerformance({ agentId }) {
  const tr = useTr();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get(`/admin/agents/${agentId}/support-performance`, { params: { days } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [agentId, days]);
  if (d === null) return <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>;
  if (d === false) return <div className="py-12 text-center text-sm text-slate-500">{tr("Accès refusé ou données indisponibles.", "Access denied or data unavailable.")}</div>;
  const max = Math.max(1, ...Object.values(d.distribution));
  return (
    <div className="space-y-4" data-testid="support-performance">
      <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={inputDark + " sm:!w-auto"} data-testid="perf-period">
        {[7, 30, 90, 365].map((n) => <option key={n} value={n}>{tr(`${n} derniers jours`, `Last ${n} days`)}</option>)}
      </select>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label={tr("Conversations traitées", "Conversations handled")} value={d.conversations} hint={`${d.resolved} ${tr("résolues", "resolved")}`} testid="perf-conversations" />
        <Kpi label={tr("1re réponse (moy.)", "First response (avg)")} value={fmtMin(d.first_response_minutes, tr)} hint={tr(`Création → 1re réponse staff · ${d.first_response_sample} conv.`, `Creation → first staff reply · ${d.first_response_sample} conv.`)} testid="perf-frt" />
        <Kpi label={tr("Résolution (moy.)", "Resolution (avg)")} value={fmtMin(d.resolution_minutes, tr)} hint={tr(`Création → résolution · ${d.resolution_sample} conv.`, `Creation → resolution · ${d.resolution_sample} conv.`)} testid="perf-resolution" />
        <Kpi label={tr("Évaluations", "Ratings")} value={d.ratings} testid="perf-ratings" />
        <Kpi label={tr("Note moyenne", "Average rating")} value={d.average_rating != null ? `${d.average_rating} / 5` : "—"} hint={d.average_rating == null ? tr(`Échantillon insuffisant (min. ${d.min_sample})`, `Not enough ratings (min. ${d.min_sample})`) : null} testid="perf-average" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ACard title={tr("Répartition des notes", "Rating distribution")}>
          <div className="space-y-2 p-4">{[5, 4, 3, 2, 1].map((s) => (
            <div key={s} className="flex items-center gap-2 text-sm" data-testid={`perf-dist-${s}`}>
              <span className="flex w-10 items-center gap-0.5 text-slate-300">{s}<Star className="h-3 w-3 fill-amber-400 text-amber-400" /></span>
              <div className="h-2 flex-1 rounded-full bg-slate-800"><div className="h-2 rounded-full bg-amber-400" style={{ width: `${(d.distribution[s] / max) * 100}%` }} /></div>
              <span className="w-8 text-right text-slate-400">{d.distribution[s]}</span>
            </div>
          ))}</div>
        </ACard>
        <ACard title={tr("Évolution par semaine", "Weekly trend")}>
          <div className="divide-y divide-slate-800 p-2 text-sm">
            {!d.trend.length && <div className="p-4 text-center text-slate-500">{tr("Aucune évaluation sur la période.", "No ratings in this period.")}</div>}
            {d.trend.map((w) => <div key={w.week} className="flex justify-between px-2 py-2"><span className="text-slate-300">{w.week}</span><span className="text-slate-400">{w.count} · {w.average != null ? `${w.average}/5` : tr("échantillon faible", "small sample")}</span></div>)}
          </div>
        </ACard>
      </div>
      <ACard title={tr("Dernières évaluations", "Latest ratings")}>
        <div className="divide-y divide-slate-800 text-sm" data-testid="perf-recent">
          {!d.recent.length && <div className="p-4 text-center text-slate-500">{tr("Aucune évaluation.", "No ratings.")}</div>}
          {d.recent.map((r, i) => <div key={i} className="flex flex-wrap justify-between gap-2 px-4 py-2.5"><span className="text-white">{r.rating}/5 · #{r.ticket_ref}{r.comment && <span className="text-slate-400"> — « {r.comment} »</span>}</span><span className="text-xs text-slate-500">{fmtDate(r.created_at, tr.lang)}</span></div>)}
        </div>
      </ACard>
    </div>
  );
}
